import type { CSSProperties, ReactNode } from 'react';

type Align = 'start' | 'center' | 'end' | 'stretch';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around';

interface StackProps {
  gap?: number;
  align?: Align;
  justify?: Justify;
  wrap?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

const ALIGN_MAP: Record<Align, CSSProperties['alignItems']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
};
const JUSTIFY_MAP: Record<Justify, CSSProperties['justifyContent']> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
};

/**
 * Vertical flex layout primitive. Replaces:
 *
 *   <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
 *
 * with:
 *
 *   <Stack gap={12}>
 *
 * Use `align` / `justify` for cross/main axis. `wrap` is allowed but rare in
 * a vertical stack — it's there for layouts that span columns on wide screens
 * and stack on narrow ones via a media query at the parent.
 */
export function Stack({
  gap = 12,
  align,
  justify,
  wrap = false,
  className,
  style,
  children,
}: StackProps) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap,
        alignItems: align ? ALIGN_MAP[align] : undefined,
        justifyContent: justify ? JUSTIFY_MAP[justify] : undefined,
        flexWrap: wrap ? 'wrap' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Horizontal flex layout primitive. Defaults to `align="center"` because >90%
 * of horizontal rows want centered cross-axis alignment.
 */
export function Inline({
  gap = 8,
  align = 'center',
  justify,
  wrap = false,
  className,
  style,
  children,
}: StackProps) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap,
        alignItems: ALIGN_MAP[align],
        justifyContent: justify ? JUSTIFY_MAP[justify] : undefined,
        flexWrap: wrap ? 'wrap' : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}
